import { FileNameCell } from '@/features/import_export_histories/Table/Columns/Cells/FileNameCell';
import { FileSizeCell } from '@/features/import_export_histories/Table/Columns/Cells/FileSizeCell';
import { StatusCell } from '@/features/import_export_histories/Table/Columns/Cells/StatusCell';
import { TypeCell } from '@/features/import_export_histories/Table/Columns/Cells/TypeCell';
import { sortByToSortOrder } from '@/features/import_export_histories/Table/helpers/toSortBy';
import {
  ImportExportHistoriesColumn,
  OnCancelClick,
  OnDetailsClick,
  OnDownloadClick,
} from '@/features/import_export_histories/type';
import { useTranslation } from '@/i18n';
import { ColumnType, SortOrder } from 'antd/es/table/interface';
import classnames from 'classnames/bind';
import { formatDateWithTimeNoSecond } from 'date-util';
import { useCallback, useMemo } from 'react';
import styles from './column.module.scss';

const cx = classnames.bind(styles);

const SORT_DIRECTIONS: SortOrder[] = ['descend', 'ascend'];

type UseImportExportHistoriesColumnsProps = {
  onDownloadClick?: OnDownloadClick;
  onCancelClick?: OnCancelClick;
  onDetailsClick?: OnDetailsClick;
  isLoading?: boolean;
  sortBy?: string;
};

export const useImportExportHistoriesColumns = ({
  onDownloadClick,
  onCancelClick,
  onDetailsClick,
  isLoading = false,
  sortBy,
}: UseImportExportHistoriesColumnsProps): ColumnType<ImportExportHistoriesColumn>[] => {
  const { t } = useTranslation();

  const columnSort = useMemo(() => sortByToSortOrder(sortBy), [sortBy]);

  const sorterConfig = useMemo(
    () =>
      !isLoading
        ? {
            sorter: true,
            sortDirections: SORT_DIRECTIONS,
            showSorterTooltip: true,
          }
        : { showSorterTooltip: false },
    [isLoading]
  );

  const handleCancelClick = useCallback(
    (id: string) => async () => {
      await onCancelClick?.(id);
    },
    [onCancelClick]
  );

  const handleDetailsClick = useCallback(
    (id: string) => () => {
      onDetailsClick?.(id);
    },
    [onDetailsClick]
  );

  return useMemo(
    () => [
      {
        title: t('import_export_history_type'),
        dataIndex: 'type',
        key: 'type',
        width: 104,
        className: cx('type-column'),
        render: (_: unknown, row: ImportExportHistoriesColumn) => (
          <TypeCell type={row.type} />
        ),
      },
      {
        title: t('import_export_history_target_data'),
        dataIndex: 'target_data',
        key: 'target_data',
        className: cx('target-data-column'),
        render: (_: unknown, row: ImportExportHistoriesColumn) => (
          <span>{t(row.target_data)}</span>
        ),
      },
      {
        title: t('import_export_history_status'),
        dataIndex: 'status',
        key: 'status',
        width: 176,
        align: 'left',
        className: cx('status-column'),
        render: (_: unknown, row: ImportExportHistoriesColumn) => (
          <StatusCell
            status={row.status}
            canCancel={row.can_cancel}
            onCancelClick={handleCancelClick(row.id)}
            onDetailsClick={handleDetailsClick(row.id)}
            type={row.type}
            errorMessage={row.error_message}
            downloadFileId={row.id}
            downloadFileName={row.name}
            canDownload={row.can_download}
            onDownloadClick={onDownloadClick}
          />
        ),
      },
      {
        title: t('import_export_history_executed_by'),
        dataIndex: 'executed_by',
        key: 'executed_by',
        className: cx('executor-column'),
        render: (_: unknown, row: ImportExportHistoriesColumn) => (
          <span>{row.executed_by}</span>
        ),
      },
      {
        title: t('import_export_history_file_name'),
        dataIndex: 'name',
        key: 'name',
        className: cx('file-name-column'),
        render: (_: unknown, row: ImportExportHistoriesColumn) => (
          <FileNameCell row={row} onDownloadClick={onDownloadClick} />
        ),
      },
      {
        title: t('import_export_history_file_size'),
        dataIndex: 'byte_size',
        key: 'byte_size',
        width: 160,
        align: 'right',
        className: cx('byte-size-column'),
        render: (_: unknown, row: ImportExportHistoriesColumn) => (
          <FileSizeCell row={row} />
        ),
      },
      {
        title: t('import_export_history_executed_at'),
        dataIndex: 'executed_at',
        key: 'executed_at',
        width: 128,
        className: cx('executed-at-column'),
        render: (value: string) => formatDateWithTimeNoSecond(value),
        ...sorterConfig,
        ...(columnSort?.field === 'executed_at' && {
          sortOrder: columnSort.order,
        }),
      },
      {
        title: t('import_export_history_completed_at'),
        dataIndex: 'completed_at',
        key: 'completed_at',
        width: 128,
        className: cx('completed-at-column'),
        render: (value: string, row: ImportExportHistoriesColumn) =>
          row.status === 'processing'
            ? null
            : formatDateWithTimeNoSecond(value),
        ...sorterConfig,
        ...(columnSort?.field === 'completed_at' && {
          sortOrder: columnSort.order,
        }),
      },
    ],
    [
      t,
      handleCancelClick,
      handleDetailsClick,
      onDownloadClick,
      sorterConfig,
      columnSort,
    ]
  );
};
